import * as echarts from "echarts";
import { useLayoutEffect, useRef } from "react";

import { IBarProps } from "../types";

interface IProps extends IBarProps {
    selectedBarColor?: string;
    isZoomed?: boolean;
}

const useBarChartZoom = ({ xData, yData, selectedBarColor, category, isZoomed = false }: IProps) => {
    const barChartSingle = useRef(null);

    useLayoutEffect(() => {
        if (!xData || !yData) return;

        // 等待 DOM 元素準備好
        const initChart = () => {
            if (!barChartSingle.current) {
                setTimeout(initChart, 10);
                return;
            }

            const barChart = echarts.init(barChartSingle.current);

            const option = {
                title: {
                    text: category,
                    left: "center",
                    top: "5%",
                    textStyle: {
                        fontSize: isZoomed ? 24 : 16,
                        fontWeight: "bold",
                    },
                },
                tooltip: {
                    trigger: "axis",
                    textStyle: {
                        fontSize: isZoomed ? 16 : 14,
                    },
                    axisPointer: {
                        type: "shadow",
                    },
                },
                grid: {
                    left: isZoomed ? "8%" : "3%",
                    right: isZoomed ? "8%" : "4%",
                    top: "15%",
                    bottom: isZoomed ? "10%" : "3%",
                    containLabel: true,
                },
                xAxis: {
                    type: "category",
                    boundaryGap: true,
                    data: xData,
                    axisLabel: {
                        fontSize: isZoomed ? 14 : 12,
                        rotate: isZoomed ? 0 : 45,
                    },
                    axisLine: {
                        lineStyle: {
                            color: "#666",
                        },
                    },
                },
                yAxis: {
                    type: "value",
                    axisLabel: {
                        fontSize: isZoomed ? 14 : 12,
                    },
                    axisLine: {
                        lineStyle: {
                            color: "#666",
                        },
                    },
                    splitLine: {
                        lineStyle: {
                            color: "#e0e0e0",
                            type: "dashed",
                        },
                    },
                },
                series: [
                    {
                        name: category,
                        data: yData,
                        type: "bar",
                        barWidth: isZoomed ? "60%" : "auto",
                        emphasis: {
                            focus: "series",
                            itemStyle: {
                                shadowBlur: 10,
                                shadowColor: "rgba(0, 0, 0, 0.3)",
                            },
                        },
                        itemStyle: {
                            color: selectedBarColor || "#5470c6",
                            borderRadius: isZoomed ? [4, 4, 0, 0] : [2, 2, 0, 0],
                        },
                        label: {
                            show: isZoomed,
                            position: "top",
                            fontSize: 12,
                            fontWeight: "bold",
                        },
                    },
                ],
                dataZoom: isZoomed
                    ? [
                          {
                              type: "slider",
                              show: true,
                              xAxisIndex: [0],
                              start: 0,
                              end: 100,
                          },
                          {
                              type: "inside",
                              xAxisIndex: [0],
                              start: 0,
                              end: 100,
                          },
                      ]
                    : undefined,
            };

            try {
                barChart.setOption(option);
            } catch (error) {
                return;
            }

            // 響應式調整
            const handleResize = () => {
                if (barChart && !barChart.isDisposed()) {
                    try {
                        barChart.resize();
                    } catch (error) {
                        // 靜默處理錯誤
                    }
                }
            };

            window.addEventListener("resize", handleResize);

            return () => {
                window.removeEventListener("resize", handleResize);
                if (barChart && !barChart.isDisposed()) {
                    try {
                        barChart.dispose();
                    } catch (error) {
                        // 靜默處理錯誤
                    }
                }
            };
        };

        initChart();
    }, [yData, category, xData, selectedBarColor, isZoomed]);

    return barChartSingle;
};

export default useBarChartZoom;
